/* Scoped to the native founding-story/reviews sections. Vendor videos keep their own lifecycle. */
(function () {
  if (window.DalstrongNativeSectionVideo) return;

  const sectionSelector = '[data-native-video-section]';
  const controllers = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reduceMotion = () => motion.matches || document.documentElement.classList.contains('a11y-reduce-motion');

  function manageVideos(container, activeVideo) {
    const videos = new Map();
    const pausePreferences = new Map();
    let destroyed = false;
    const nearObserver = window.IntersectionObserver && new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const state = videos.get(entry.target);
        if (state) state.near = entry.isIntersecting;
      });
      update();
    }, { rootMargin: '300px 0px' });
    const viewObserver = window.IntersectionObserver && new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const state = videos.get(entry.target);
        if (state) state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      });
      update();
    }, { threshold: [0, 0.1] });

    function load(video, state) {
      if (state.loaded) return;
      state.loaded = true;
      video.querySelectorAll('source[data-src]').forEach(source => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.preload = 'metadata';
      video.load();
    }

    function stop(video, state) {
      // pause events are asynchronous. Only suppress events caused by this controller.
      if (!video.paused) {
        state.automaticPauses += 1;
        video.pause();
      }
      state.userPlaying = false;
    }

    function update() {
      if (destroyed) return;
      const active = activeVideo();
      videos.forEach((state, video) => {
        if (state.near) load(video, state);
        if (video !== active || !state.visible || document.hidden || (reduceMotion() && !state.userPlaying)) {
          stop(video, state);
          return;
        }
        if (!viewObserver || state.pausePreference.paused || state.blocked || state.autoRequested || !video.paused || reduceMotion()) return;
        load(video, state);
        state.autoRequested = true;
        // A rejected autoplay leaves native controls available; do not retry on every callback.
        Promise.resolve(video.play()).catch(error => {
          // Leaving the viewport can interrupt a pending play; that is not an autoplay denial.
          if (error.name !== 'AbortError') state.blocked = true;
        }).finally(() => {
          state.autoRequested = false;
          update();
        });
      });
    }

    function refresh() {
      if (destroyed) return;
      videos.forEach((state, video) => {
        if (container.contains(video)) return;
        stop(video, state);
        if (nearObserver) nearObserver.unobserve(video);
        if (viewObserver) viewObserver.unobserve(video);
        state.removeListeners();
        videos.delete(video);
      });
      container.querySelectorAll('video[data-native-section-video]').forEach(video => {
        if (videos.has(video)) return;
        const key = video.dataset.nativeVideoKey || video;
        if (!pausePreferences.has(key)) pausePreferences.set(key, { paused: false });
        const state = { near: false, visible: !viewObserver, loaded: false, pausePreference: pausePreferences.get(key),
          userPlaying: false, automaticPauses: 0, autoRequested: false, blocked: false };
        const prepare = () => load(video, state);
        const onPause = () => {
          if (state.automaticPauses) state.automaticPauses -= 1;
          else { state.pausePreference.paused = true; state.userPlaying = false; }
        };
        const onPlay = () => {
          if (!state.autoRequested) {
            state.pausePreference.paused = false;
            state.blocked = false;
            state.userPlaying = true;
          }
          if (video !== activeVideo() || document.hidden || (viewObserver && !state.visible)) stop(video, state);
        };
        video.addEventListener('pointerdown', prepare);
        video.addEventListener('keydown', prepare);
        video.addEventListener('play', onPlay);
        video.addEventListener('pause', onPause);
        state.removeListeners = () => {
          video.removeEventListener('pointerdown', prepare);
          video.removeEventListener('keydown', prepare);
          video.removeEventListener('play', onPlay);
          video.removeEventListener('pause', onPause);
        };
        videos.set(video, state);
        if (nearObserver) nearObserver.observe(video);
        if (viewObserver) viewObserver.observe(video);
        // Without IntersectionObserver, keep the poster and native click-to-play controls.
      });
      update();
    }

    refresh();
    return { update, refresh, destroy() {
      destroyed = true;
      if (nearObserver) nearObserver.disconnect();
      if (viewObserver) viewObserver.disconnect();
      videos.forEach((state, video) => { stop(video, state); state.removeListeners(); });
      videos.clear();
    } };
  }

  function foundingStory(container) {
    const dom = {
      sidebar: container.querySelector('.fs-sidebar'),
      tabs: Array.from(container.querySelectorAll('.desktop-sidebar .sidebar-item')),
      texts: Array.from(container.querySelectorAll('.fs-text-block')),
      medias: Array.from(container.querySelectorAll('.fs-media-block')),
      testimonials: Array.from(container.querySelectorAll('.feature-item')),
      featuresContainer: container.querySelector('.founding-story-features'),
      indicator: container.querySelector('.progress-indicator'),
      fill: container.querySelector('.progress-fill'),
      mobileSwiperEl: container.querySelector('.mobile-content-swiper')
    };

    const state = {
      activeIndex: 0,
      swiperInstance: null,
      totalTabs: dom.tabs.length,
      totalTestimonials: dom.testimonials.length,
      isMobile: window.innerWidth <= 900,
      indicatorTimeout: null
    };

    function updateIndicator() {
      if (!dom.indicator || state.totalTabs === 0) return;

      if (state.isMobile) {
        const percentage = (state.activeIndex / (state.totalTabs - 1 || 1)) * 100;
        
        dom.indicator.style.left = `${percentage}%`;
        dom.indicator.style.top = '50%';
        dom.fill.style.width = `${percentage}%`;
        dom.fill.style.height = '2px';
        
        let translateX = 0;
        if (percentage === 50) translateX = -50;
        if (percentage === 100) translateX = -100;
        
        dom.indicator.style.transform = `translate(${translateX}%, -50%)`;

      } else {
        const calculatePercent = (state.activeIndex + 1) * 25;
        
        dom.indicator.style.top = `${calculatePercent}%`;
        dom.indicator.style.left = '50%';
        dom.fill.style.height = `${calculatePercent}%`;
        dom.fill.style.width = '2px';
        dom.indicator.style.transform = `translate(-50%, -50%)`;
      }
    }

    function updateTestimonials(index) {
      if (state.totalTestimonials === 0) return;

      dom.featuresContainer.classList.remove('animating');
      void dom.featuresContainer.offsetWidth;
      dom.featuresContainer.classList.add('animating');

      const targetIndex = index % state.totalTestimonials;
      const prevIndex = (targetIndex - 1 + state.totalTestimonials) % state.totalTestimonials;

      dom.testimonials.forEach((t, i) => {
        t.classList.remove('active');
        let order = 3;
        if (i === targetIndex) {
          order = 2;
          t.classList.add('active');
        } else if (i === prevIndex) {
          order = 1;
        }
        t.style.order = order;
      });
    }

    const playback = manageVideos(container, () => {
      const activeMedia = state.isMobile
        ? dom.mobileSwiperEl && dom.mobileSwiperEl.querySelector('.mobile-content-slide.swiper-slide-active')
        : dom.medias[state.activeIndex];
      return activeMedia && activeMedia.querySelector('video[data-native-section-video]');
    });

    function updateActiveVideo() { playback.update(); }

    function switchToTab(index) {
      if (index < 0 || index >= state.totalTabs) return;
      state.activeIndex = index;

      const updateActive = (elements) => {
        elements.forEach((el, i) => el.classList.toggle('active', i === index));
      };

      updateActive(dom.tabs);
      updateActive(dom.texts);
      updateActive(dom.medias);

      if (state.swiperInstance && state.swiperInstance.activeIndex !== index) {
        state.swiperInstance.slideTo(index);
      }

      updateTestimonials(index);
      requestAnimationFrame(() => updateIndicator());
      clearTimeout(state.indicatorTimeout);
      state.indicatorTimeout = setTimeout(() => updateIndicator(), 550);
      updateActiveVideo();
    }

    function initSwiper() {
      const shouldBeInitialized = state.isMobile;

      if (shouldBeInitialized && !state.swiperInstance && dom.mobileSwiperEl) {
        state.swiperInstance = new Swiper(dom.mobileSwiperEl, {
          slidesPerView: 1,
          autoHeight: true,
          on: {
            slideChange: function() {
                switchToTab(this.activeIndex);
            }
          }
        });
        state.swiperInstance.slideTo(state.activeIndex, 0);
      } else if (!shouldBeInitialized && state.swiperInstance) {
        state.swiperInstance.destroy(true, true);
        state.swiperInstance = null;
      }
    }

    const tabListeners = dom.tabs.map((tab, index) => {
      const listener = () => switchToTab(index);
      tab.addEventListener('click', listener);
      return () => tab.removeEventListener('click', listener);
    });

    const onResize = () => {
      const newIsMobile = window.innerWidth <= 900;
      const breakpointChanged = newIsMobile !== state.isMobile;
      state.isMobile = newIsMobile;

      initSwiper();

      updateIndicator();
      if (breakpointChanged) updateActiveVideo();
    };
    window.addEventListener('resize', onResize);

    const initialActive = dom.tabs.findIndex(t => t.classList.contains('active'));
    switchToTab(initialActive !== -1 ? initialActive : 0);
    initSwiper();

    return {
      update: playback.update,
      refresh: playback.refresh,
      destroy() {
        playback.destroy();
        tabListeners.forEach(remove => remove());
        window.removeEventListener('resize', onResize);
        clearTimeout(state.indicatorTimeout);
        if (state.swiperInstance) state.swiperInstance.destroy(true, true);
      }
    };
  }

  function reviews(container) {
    const element = container.querySelector('.review-with-video--swiper');
    let selectedVideo = null;
    let selectingVideo = false;
    const swiper = new Swiper(element, {
      slidesPerView: 'auto', spaceBetween: 15, watchSlidesProgress: true,
      navigation: {
        nextEl: container.querySelector('.swiper-button-next'),
        prevEl: container.querySelector('.swiper-button-prev')
      },
      pagination: { el: container.querySelector('.swiper-pagination'), type: 'progressbar' }
    });
    const playback = manageVideos(container, () => {
      if (selectedVideo && container.contains(selectedVideo)) return selectedVideo;
      const slide = swiper.slides[swiper.activeIndex];
      return slide && slide.querySelector('video[data-native-section-video]');
    });
    // Native controls on an adjacent visible card make that card the active one.
    const activatePlayedSlide = event => {
      if (!event.target.matches('video[data-native-section-video]')) return;
      const index = Array.from(swiper.slides).indexOf(event.target.closest('.swiper-slide'));
      if (index < 0) return;
      selectedVideo = event.target;
      if (index !== swiper.activeIndex) {
        selectingVideo = true;
        swiper.slideTo(index);
        selectingVideo = false;
      }
      // Finish the target's play event before reconciling its user-play state.
      queueMicrotask(playback.update);
    };
    container.addEventListener('play', activatePlayedSlide, true);
    swiper.on('slideChange', () => {
      if (selectingVideo) return;
      selectedVideo = null;
      playback.update();
    });
    swiper.on('slideChangeTransitionEnd', playback.update);
    return { update: playback.update, refresh() { swiper.update(); playback.refresh(); }, destroy() {
      playback.destroy();
      container.removeEventListener('play', activatePlayedSlide, true);
      swiper.destroy(true, true);
    } };
  }

  function scan(root) {
    if (!window.Swiper) return;
    const sections = [];
    if (root.matches && root.matches(sectionSelector)) sections.push(root);
    if (root.querySelectorAll) sections.push(...root.querySelectorAll(sectionSelector));
    sections.forEach(section => {
      if (controllers.has(section)) return;
      controllers.set(section, section.dataset.nativeVideoSection === 'founding-story'
        ? foundingStory(section) : reviews(section));
    });
  }
  function sweep() {
    controllers.forEach((controller, section) => {
      if (section.isConnected) return;
      controller.destroy();
      controllers.delete(section);
    });
  }
  function updateAll() { controllers.forEach(controller => controller.update()); }
  function start() {
    scan(document);
    new MutationObserver(records => {
      sweep();
      const refresh = new Set();
      records.forEach(record => {
        record.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
        const section = record.target.closest && record.target.closest(sectionSelector);
        // Swiper may insert its own wrappers/navigation. Refresh only for native media changes.
        const changedVideo = [...record.addedNodes, ...record.removedNodes].some(node =>
          node.nodeType === 1 && (node.matches('video[data-native-section-video]') ||
            node.querySelector('video[data-native-section-video]')));
        if (section && changedVideo) refresh.add(section);
      });
      refresh.forEach(section => { const controller = controllers.get(section); if (controller) controller.refresh(); });
    }).observe(document.body, { childList: true, subtree: true });
    new MutationObserver(updateAll).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', updateAll);
    if (motion.addEventListener) motion.addEventListener('change', updateAll);
    else motion.addListener(updateAll);
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((controller, section) => {
      if (!event.target.contains(section)) return;
      controller.destroy();
      controllers.delete(section);
    });
  });
  document.addEventListener('page:loaded', () => scan(document));
  window.DalstrongNativeSectionVideo = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
}());
